import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { ScreenPermission } from '../types';

interface PermissionsContextType {
  permissions: ScreenPermission[];
  setPermissions: (permissions: ScreenPermission[]) => void;
  hasPermission: (screenCode: string, action?: 'view' | 'create' | 'edit' | 'delete' | 'export') => boolean;
  canAccessScreen: (screenCode: string) => boolean;
  clearPermissions: () => void;
  setLocationPermissions: (permissions: ScreenPermission[] | null) => void;
  setLocationLoading: (loading: boolean) => void;
  locationLoading: boolean;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

interface PermissionsProviderProps {
  children: ReactNode;
}

export const PermissionsProvider: React.FC<PermissionsProviderProps> = ({ children }) => {
  const [permissions, setPermissionsState] = useState<ScreenPermission[]>([]);
  const [locationPermissions, setLocationPermissions] = useState<ScreenPermission[] | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);

  const setPermissions = useCallback((newPermissions: ScreenPermission[]) => {
    setPermissionsState(newPermissions);
    // Store in localStorage for persistence across page reloads
    localStorage.setItem('userPermissions', JSON.stringify(newPermissions));
  }, []);

  const clearPermissions = useCallback(() => {
    setPermissionsState([]);
    setLocationPermissions(null);
    setLocationLoading(false);
    localStorage.removeItem('userPermissions');
  }, []);

  const hasPermission = useCallback((screenCode: string, action: 'view' | 'create' | 'edit' | 'delete' | 'export' = 'view'): boolean => {
    const current = locationPermissions ?? permissions;
    const permission = current.find(p => p.screen_code === screenCode)
      || (screenCode === 'SENSORS_DEVICES' ? current.find(p => p.screen_code === 'ACTIONS') : undefined);
    if (!permission) return false;

    switch (action) {
      case 'view':
        return permission.can_view;
      case 'create':
        return permission.can_create;
      case 'edit':
        return permission.can_edit;
      case 'delete':
        return permission.can_delete;
      case 'export':
        return permission.can_export;
      default:
        return false;
    }
  }, [permissions, locationPermissions]);

  const canAccessScreen = useCallback((screenCode: string): boolean => {
    return hasPermission(screenCode, 'view');
  }, [hasPermission]);

  // Load permissions from localStorage on mount
  React.useEffect(() => {
    const storedPermissions = localStorage.getItem('userPermissions');
    if (storedPermissions) {
      try {
        const parsed = JSON.parse(storedPermissions);
        setPermissionsState(parsed);
      } catch (error) {
        console.error('Failed to parse stored permissions:', error);
        localStorage.removeItem('userPermissions');
      }
    }
  }, []);

  const value: PermissionsContextType = {
    permissions,
    setPermissions,
    hasPermission,
    canAccessScreen,
    clearPermissions,
    setLocationPermissions,
    setLocationLoading,
    locationLoading,
  };

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};

export const usePermissions = (): PermissionsContextType => {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};
